<template>
  <div class="reg-panel">
    <div class="panel-body">
      <div class="row g-3 align-items-start">
        <!-- Columna izquierda -->
        <div class="col-8">
          <!-- Carga de archivo -->
          <div class="search-box">
            <label class="field-label">Archivo CSV (columnas "numero;codigo", delimitador ";")</label>
            <div class="d-flex gap-1">
              <input
                ref="inputArchivo"
                type="file"
                accept=".csv"
                class="search-input"
                @change="onArchivoChange"
              />
              <button
                type="button"
                class="btn-reload"
                @click="$refs.inputArchivo.click()"
                title="Seleccionar archivo"
              >
                <span class="material-symbols-outlined" style="font-size:18px">upload_file</span>
              </button>
            </div>
            <div class="search-status mt-1">
              <span v-if="archivoCsv" class="text-muted">
                <span class="material-symbols-outlined" style="font-size:14px; vertical-align:-2px">description</span>
                {{ archivoCsv.name }}
                <span v-if="lineasCsv !== null">— {{ lineasCsv }} fila(s) detectada(s)</span>
              </span>
              <span v-else class="text-muted">Ningún archivo seleccionado</span>
            </div>
          </div>

          <!-- Motivo -->
          <div class="search-box mt-3">
            <label class="field-label">Motivo</label>
            <input
              type="text"
              class="search-input"
              v-model="motivo"
              maxlength="150"
              placeholder="Motivo de inactivación..."
            />
          </div>

          <!-- Log de resultados (RAISE NOTICE / RAISE WARNING del procedimiento) -->
          <div v-if="mensajes.length" class="tabla-seleccionados mt-3">
            <div class="tabla-header" style="grid-template-columns: 1fr;">
              <span>
                {{ ultimaEjecucionSimulada ? 'Resultado de la simulación' : 'Resultado de la ejecución' }}
              </span>
            </div>
            <div class="log-box">
              <div v-for="(m, i) in mensajes" :key="i" class="log-line">{{ m }}</div>
            </div>
          </div>
          <div v-else class="tabla-vacia mt-3">
            <span class="material-symbols-outlined" style="font-size:28px; color:#ced4da">upload_file</span>
            <p class="mt-1 mb-0">Seleccione un CSV y ejecute la vista previa</p>
          </div>
        </div>

        <!-- Columna derecha: acciones -->
        <div class="col-4 d-flex flex-column gap-3" style="padding-top:2rem;">
          <div class="resumen-box">
            <div class="resumen-row">
              <span>Trámite</span>
              <strong class="text-primary">{{ idTramite }}</strong>
            </div>
            <div class="resumen-row" v-if="resumen.encontrados !== null">
              <span>Detalles encontrados</span>
              <strong>{{ resumen.encontrados }}</strong>
            </div>
            <div class="resumen-row" v-if="resumen.inactivados !== null">
              <span>Detalles {{ ultimaEjecucionSimulada ? 'a inactivar' : 'inactivados OK' }}</span>
              <strong>{{ resumen.inactivados }}</strong>
            </div>
            <div class="resumen-row" v-if="resumen.conError !== null">
              <span>Detalles con error</span>
              <strong :class="resumen.conError > 0 ? 'text-danger' : ''">{{ resumen.conError }}</strong>
            </div>
            <div class="resumen-row" v-if="resumen.planillasAnuladas !== null">
              <span>Planillas anuladas</span>
              <strong>{{ resumen.planillasAnuladas }}</strong>
            </div>
          </div>

          <button
            type="button" class="btn-action secondary"
            @click="ejecutar(true)"
            :disabled="!archivoCsv || procesando"
            title="Previsualizar sin persistir cambios"
          >
            <span class="material-symbols-outlined">visibility</span>
            Vista previa (simular)
          </button>

          <button
            type="button" class="btn-action primary"
            @click="confirmarEjecucionReal"
            :disabled="!puedeEjecutarReal"
            title="Ejecutar la inactivación de forma definitiva"
          >
            <span class="material-symbols-outlined">block</span>
            Ejecutar definitivamente
          </button>

          <button
            type="button" class="btn-action secondary"
            @click="limpiar"
            title="Limpiar selección"
          >
            <span class="material-symbols-outlined">delete</span>
            Limpiar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import Swal from "sweetalert2";

export default {
  name: "InactivarItemsTramite",
  props: {
    idTramite: { type: [Number, String], required: true },
    idUsuario: { type: [Number, String], required: true },
  },
  emits: ["ejecutado"],
  data() {
    return {
      archivoCsv: null,
      lineasCsv: null,
      motivo: "Inactivación por auditoria tramite",
      procesando: false,
      mensajes: [],
      ultimaEjecucionSimulada: null,
      previewOk: false,
      resumen: {
        encontrados: null,
        inactivados: null,
        conError: null,
        planillasAnuladas: null,
      },
    };
  },
  computed: {
    puedeEjecutarReal() {
      return (
        this.archivoCsv &&
        this.previewOk &&
        !this.procesando &&
        (this.resumen.conError === null || this.resumen.conError === 0)
      );
    },
  },
  methods: {
    onArchivoChange(e) {
      const file = e.target.files[0] || null;
      this.archivoCsv = file;
      this.previewOk = false;
      this.mensajes = [];
      this.resetResumen();
      this.lineasCsv = null;
      if (file) this.contarLineas(file);
    },
    contarLineas(file) {
      const reader = new FileReader();
      reader.onload = () => {
        const texto = String(reader.result || "");
        const filas = texto
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter((l) => l.length > 0);
        this.lineasCsv = Math.max(filas.length - 1, 0);
      };
      reader.readAsText(file, "UTF-8"); // coherente con ENCODING UTF8 del procedimiento
    },
    resetResumen() {
      this.resumen = { encontrados: null, inactivados: null, conError: null, planillasAnuladas: null };
    },
    parseResumen(mensajes) {
      const buscar = (regex) => {
        for (const m of mensajes) {
          const match = m.match(regex);
          if (match) return Number(match[1]);
        }
        return null;
      };
      this.resumen.encontrados = buscar(/Detalles encontrados\s*:\s*(\d+)/i);
      this.resumen.inactivados = buscar(/Detalles inactivados OK\s*:\s*(\d+)/i);
      this.resumen.conError = buscar(/Detalles con error\s*:\s*(\d+)/i);
      this.resumen.planillasAnuladas = buscar(/Planillas anuladas\s*:\s*(\d+)/i);
    },
    async confirmarEjecucionReal() {
      const confirm = await Swal.fire({
        title: "¿Confirmar inactivación definitiva?",
        html: `Se inactivarán <strong>${this.resumen.inactivados ?? "?"}</strong> detalle(s)
               y podrían anularse <strong>${this.resumen.planillasAnuladas ?? "?"}</strong> planilla(s)
               en el trámite <strong>${this.idTramite}</strong>.<br><br>
               Motivo: <em>${this.motivo}</em><br><br>
               Esta acción <strong>no se puede deshacer</strong>.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, ejecutar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#dc3545",
      });
      if (!confirm.isConfirmed) return;
      await this.ejecutar(false);
    },
    async ejecutar(simular) {
      if (!this.archivoCsv) return;
      if (!this.motivo || !this.motivo.trim()) {
        Swal.fire("Falta el motivo", "Ingrese un motivo antes de continuar.", "warning");
        return;
      }
      this.procesando = true;
      this.mensajes = [];
      try {
        const formData = new FormData();
        formData.append("idTramite", Number(this.idTramite));
        formData.append("idUsuario", Number(this.idUsuario));
        formData.append("motivo", this.motivo);
        formData.append("simular", simular);
        formData.append("archivo", this.archivoCsv);

        const res = await api.post("/procedimientos/inactivar-items-tramite", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        this.mensajes = res.data?.mensajes ?? [];
        this.ultimaEjecucionSimulada = simular;
        this.parseResumen(this.mensajes);

        if (simular) {
          this.previewOk = true;
          if ((this.resumen.conError ?? 0) > 0) {
            Swal.fire(
              "Simulación con errores",
              `Se detectaron ${this.resumen.conError} error(es) en el detalle. Revise el log antes de ejecutar.`,
              "warning"
            );
          } else if ((this.resumen.encontrados ?? 0) === 0) {
            Swal.fire(
              "Sin coincidencias",
              "No se encontraron ítems activos que coincidan con el CSV en este trámite.",
              "info"
            );
          } else {
            Swal.fire("Vista previa lista", "Revise el resumen y ejecute cuando esté listo.", "info");
          }
        } else {
          this.previewOk = false;
          Swal.fire("Éxito", "Inactivación ejecutada correctamente.", "success");
          this.$emit("ejecutado");
        }
      } catch (e) {
        const raw = e.response?.data;
        const msg = typeof raw === "string" ? raw : raw?.message ?? raw?.error ?? e.message ?? "Error desconocido";
        Swal.fire("Error", msg, "error");
      } finally {
        this.procesando = false;
      }
    },
    limpiar() {
      this.archivoCsv = null;
      this.lineasCsv = null;
      this.mensajes = [];
      this.previewOk = false;
      this.ultimaEjecucionSimulada = null;
      this.motivo = "Inactivación por auditoria tramite";
      this.resetResumen();
      if (this.$refs.inputArchivo) this.$refs.inputArchivo.value = "";
    },
  },
};
</script>

<style scoped>
.reg-panel  { background: #f8f9fa; }
.panel-body { padding: 14px 16px; }
.search-input {
  width: 100%; padding: 6px 10px; border: 1px solid #ced4da;
  border-radius: 4px; font-size: 0.85rem; transition: border-color 0.15s;
}
.search-input:focus { outline: none; border-color: #0d6efd; box-shadow: 0 0 0 2px rgba(13,110,253,.15); }
.btn-reload {
  display: flex; align-items: center; justify-content: center;
  padding: 6px 10px; border: 1px solid #ced4da; border-radius: 4px;
  background: #fff; cursor: pointer; color: #495057; transition: all 0.15s;
}
.btn-reload:hover { background: #0d6efd; color: #fff; border-color: #0d6efd; }
.search-status { font-size: 0.78rem; }
.field-label { font-weight: 600; font-size: 0.85rem; color: #495057; display: block; margin-bottom: 4px; }
.tabla-seleccionados {
  border: 1px solid #dee2e6; border-radius: 6px; overflow: hidden; font-size: 0.82rem;
}
.tabla-header {
  display: grid;
  background: #e9ecef; padding: 5px 8px; font-weight: 700; color: #495057; gap: 6px;
}
.log-box {
  max-height: 320px; overflow-y: auto; background: #fff;
  font-family: "Courier New", monospace; font-size: 0.76rem;
}
.log-line {
  padding: 3px 8px; border-top: 1px solid #f0f0f0; white-space: pre-wrap;
}
.tabla-vacia {
  display: flex; flex-direction: column; align-items: center;
  padding: 20px; color: #adb5bd; font-size: 0.85rem;
  border: 2px dashed #dee2e6; border-radius: 6px; background: #fff;
}
.resumen-box {
  background: #fff; border: 1px solid #dee2e6;
  border-radius: 6px; padding: 10px 12px; font-size: 0.82rem;
}
.resumen-row {
  display: flex; justify-content: space-between; padding: 2px 0; color: #495057;
}
.btn-action {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 14px; border: none; border-radius: 6px;
  font-size: 0.85rem; font-weight: 600; cursor: pointer;
  transition: all 0.15s; width: 100%;
}
.btn-action.primary                        { background: #dc3545; color: #fff; }
.btn-action.primary:hover:not(:disabled)   { background: #bb2d3b; }
.btn-action.secondary                      { background: #fff; color: #6c757d; border: 1px solid #dee2e6; }
.btn-action.secondary:hover:not(:disabled) { background: #f8f9fa; }
.btn-action:disabled                       { opacity: 0.45; cursor: not-allowed; }
</style>